import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Cpu,
    ArrowRight,
    CheckCircle,
    Lightning,
    ChartLineUp,
    Wallet,
    Clock,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fmtAone, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLivePrice } from "@/context/LivePriceContext";

export default function MinerTerminalPage({ onConnect }) {
    const { user, refresh } = useAuth();
    const { price: livePrice, changePct } = useLivePrice();
    const [plans, setPlans] = useState([]);
    const [network, setNetwork] = useState(null);
    const [positions, setPositions] = useState([]);
    const [totals, setTotals] = useState({ hashrate_ghs: 0, pending_aone: 0, total_earned_aone: 0 });
    const [buying, setBuying] = useState(null);
    const [claimingAll, setClaimingAll] = useState(false);
    const [tick, setTick] = useState(0);
    const [halving, setHalving] = useState(null);
    const nav = useNavigate();

    // Live tick for pending-earn preview UI
    useEffect(() => {
        const id = setInterval(() => setTick((t) => t + 1), 1000);
        return () => clearInterval(id);
    }, []);

    // Halving polling only (price via live context; treasury numbers are hidden)
    useEffect(() => {
        const fetchMarket = () => {
            api.get("/network/halving").then((r) => setHalving(r.data)).catch(() => {});
        };
        fetchMarket();
        const id = setInterval(fetchMarket, 30000);
        return () => clearInterval(id);
    }, []);

    const loadPlans = async () => {
        const [pRes, nRes] = await Promise.all([
            api.get("/miner/plans"),
            api.get("/miner/network"),
        ]);
        setPlans(pRes.data.plans);
        setNetwork(nRes.data);
    };
    const loadPositions = async () => {
        if (!user) return;
        const { data } = await api.get("/miner/positions");
        setPositions(data.positions);
        setTotals(data.totals);
    };

    useEffect(() => { loadPlans(); }, []);
    useEffect(() => { loadPositions(); const id = setInterval(loadPositions, 20000); return () => clearInterval(id); }, [user]);

    // Live pending preview: pending + (elapsed_since_load / 86400 * dailyRate)
    const livePending = useMemo(() => {
        if (!positions.length) return 0;
        const now = Date.now();
        return positions.reduce((acc, p) => {
            if (p.status !== "active") return acc + Number(p.pending_aone || 0);
            const last = new Date(p.last_accrual_at || p.purchased_at).getTime();
            const daily = Number(p.daily_aone || 0);
            const elapsed = Math.max(0, (now - last) / 1000);
            return acc + Number(p.pending_aone || 0) + daily * (elapsed / 86400);
        }, 0);
    }, [positions, tick]);

    const buy = async (planId) => {
        if (!user) { onConnect?.(); return; }
        setBuying(planId);
        try {
            await api.post("/miner/purchase", { plan_id: planId });
            toast.success("Miner activated - hashrate contributing to network");
            await Promise.all([loadPositions(), refresh()]);
        } catch (e) {
            const msg = formatApiError(e, "Purchase failed");
            toast.error(msg);
            if (msg.toLowerCase().includes("insufficient usdt")) {
                setTimeout(() => nav("/wallet/deposit"), 800);
            }
        } finally {
            setBuying(null);
        }
    };

    const claimAll = async () => {
        setClaimingAll(true);
        try {
            const { data } = await api.post("/miner/claim-all");
            if (data.claimed_aone > 0) {
                toast.success(`Auto-swapped ${fmtAone(data.claimed_aone, 4)} AONE for $${data.usdt_credited.toFixed(4)} USDT`);
                await Promise.all([loadPositions(), refresh()]);
            } else {
                toast.info("No pending rewards yet - check back in a minute");
            }
        } catch (e) {
            toast.error(formatApiError(e, "Withdrawal failed"));
        } finally {
            setClaimingAll(false);
        }
    };

    return (
        <div className="min-h-screen">
            {/* Header */}
            <section className="border-b bg-hero-gradient" style={{ borderColor: "var(--border)" }}>
                <div className="max-w-[1200px] mx-auto px-5 sm:px-8 pt-12 pb-10 sm:pt-16 sm:pb-14">
                    <div className="chip chip-live mb-5" data-testid="miner-live-chip">Mining Live</div>
                    <h1 className="text-[36px] sm:text-[52px] font-bold leading-[1.05] tracking-[-0.035em] text-[var(--text)] max-w-3xl">
                        Miner Terminal
                    </h1>
                    <p className="mt-4 text-[16px] sm:text-[18px] text-[var(--text-2)] max-w-2xl leading-[1.55]">
                        Buy a plan with USDT. Contribute hashrate to the Aegis DePIN network. Earn AONE continuously.
                    </p>
                </div>
            </section>

            {/* Network stats */}
            <section className="border-b" style={{ borderColor: "var(--border)", background: "var(--bg-surface)" }}>
                <div className="max-w-[1200px] mx-auto px-5 sm:px-8 py-6 grid grid-cols-2 sm:grid-cols-4 gap-6">
                    <NetStat label="Network Hashrate" value={network ? `${network.total_hashrate_ghs.toFixed(1)} GH/s` : "-"} />
                    <NetStat label="Active Miners" value={network ? network.active_miners.toLocaleString() : "-"} />
                    <NetStat label="Mining Pool" value={network ? `${fmtAone(network.mining_pool_remaining)} left` : "-"} />
                    <NetStat label="AONE Price" value={`$${livePrice.toFixed(6)}`} sub={`${changePct >= 0 ? "+" : ""}${changePct.toFixed(2)}%`} live />
                </div>
            </section>

            {/* If signed in - show user dashboard */}
            {user && (
                <section className="py-10 px-5 sm:px-8">
                    <div className="max-w-[1200px] mx-auto grid md:grid-cols-4 gap-4">
                        <DashCard
                            label="Your Hashrate"
                            value={`${totals.hashrate_ghs.toFixed(1)} GH/s`}
                            icon={<Cpu size={18} weight="duotone" />}
                        />
                        <DashCard
                            label="Pending AONE"
                            value={fmtAone(livePending, 4)}
                            live
                            icon={<Lightning size={18} weight="duotone" />}
                        />
                        <DashCard
                            label="Total Mined"
                            value={fmtAone(totals.total_earned_aone, 2)}
                            icon={<ChartLineUp size={18} weight="duotone" />}
                        />
                        <DashCard
                            label="USDT Balance"
                            value={`$${fmtAone(user.usdt_balance || 0, 2)}`}
                            icon={<Wallet size={18} weight="duotone" />}
                            action={
                                <Link to="/wallet/deposit" className="text-[11px] font-medium text-[var(--primary)] hover:underline">
                                    Deposit +
                                </Link>
                            }
                        />
                    </div>
                    <div className="max-w-[1200px] mx-auto mt-4 flex flex-wrap gap-2">
                        <button
                            data-testid="miner-claim-all"
                            onClick={claimAll}
                            disabled={claimingAll || livePending <= 0}
                            className="btn-primary !text-[13px]"
                        >
                            {claimingAll ? "Auto-swapping…" : `Withdraw ${fmtAone(livePending, 4)} AONE  →  $${(livePending * livePrice).toFixed(4)} USDT`}
                            <ArrowRight size={12} />
                        </button>
                        <Link to="/wallet/deposit" data-testid="miner-deposit-cta" className="btn-ghost !text-[13px]">
                            <Wallet size={13} /> Deposit USDT
                        </Link>
                        <Link to="/referrals" data-testid="miner-referrals-link" className="btn-ghost !text-[13px]">
                            Referrals · 10% commission
                        </Link>
                    </div>
                    <div className="max-w-[1200px] mx-auto mt-4 p-4 rounded-lg text-[13px]" style={{ background: "var(--bg-surface)", border: "1px solid var(--border)" }} data-testid="miner-swap-narrative">
                        <div className="flex flex-wrap items-center gap-3 justify-between">
                            <div className="text-[var(--text-2)] leading-[1.5] max-w-3xl">
                                <span className="text-[var(--primary-glow)] font-medium">Auto-Pool.</span> Your mined AONE is
                                automatically swapped to USDT at the live market rate the moment you withdraw. The 50/50 Auto-Pool
                                reserve guarantees payouts without waiting on external liquidity.
                            </div>
                            <div className="font-mono text-[12px] flex items-center gap-3 shrink-0">
                                <span className="text-[var(--text-3)]">Live rate</span>
                                <span className="text-[var(--text)]" data-testid="miner-live-rate">1 AONE = ${livePrice.toFixed(6)}</span>
                                <span className={changePct >= 0 ? "text-[var(--success)]" : "text-[var(--danger)]"}>
                                    {changePct >= 0 ? "+" : ""}{changePct.toFixed(2)}%
                                </span>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Plans */}
            <section className="py-12 px-5 sm:px-8">
                <div className="max-w-[1200px] mx-auto">
                    {halving && (
                        <div className="mb-8 card p-5 sm:p-6 flex flex-wrap items-center justify-between gap-4" data-testid="miner-halving-card" style={{ borderColor: "var(--primary)" }}>
                            <div>
                                <div className="text-[10.5px] font-mono uppercase tracking-[0.16em] text-[var(--primary-glow)]">Halving countdown</div>
                                <div className="text-[17px] font-semibold text-[var(--text)] mt-1">Lock in {halving.current_daily_emission.toLocaleString()} AONE/day rate</div>
                                <div className="text-[13px] text-[var(--text-2)] mt-1">Rate halves to {halving.next_daily_emission.toLocaleString()} AONE/day after countdown ends</div>
                            </div>
                            <div className="text-right">
                                <div className="text-[42px] sm:text-[52px] font-extrabold font-mono tabular-nums text-[var(--primary-glow)] leading-none">{Math.max(0, halving.next_halving_days)}</div>
                                <div className="text-[12px] text-[var(--text-3)] mt-1">days remaining</div>
                            </div>
                        </div>
                    )}
                    <div className="mb-8">
                        <div className="eyebrow mb-2">Choose a Plan</div>
                        <h2 className="text-[26px] sm:text-[34px] font-bold tracking-[-0.03em] text-[var(--text)]">
                            Hashrate you can start using today
                        </h2>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                        {plans.map((p) => (
                            <PlanCard
                                key={p.id}
                                plan={p}
                                onBuy={() => buy(p.id)}
                                buying={buying === p.id}
                                disabled={!!buying}
                                signedIn={!!user}
                                onSignIn={onConnect}
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* Positions */}
            {user && positions.length > 0 && (
                <section className="pb-16 px-5 sm:px-8">
                    <div className="max-w-[1200px] mx-auto">
                        <div className="mb-6">
                            <div className="eyebrow mb-2">Your Miners</div>
                            <h2 className="text-[22px] sm:text-[28px] font-bold tracking-[-0.02em] text-[var(--text)]">
                                {positions.length} active plan{positions.length === 1 ? "" : "s"}
                            </h2>
                        </div>
                        <div className="card overflow-hidden">
                            <table className="tbl" data-testid="miner-positions-table">
                                <thead>
                                    <tr>
                                        <th>Plan</th>
                                        <th>Hashrate</th>
                                        <th>Purchased</th>
                                        <th>Expires</th>
                                        <th>Total mined</th>
                                        <th>Pending</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {positions.map((p) => (
                                        <tr key={p.id}>
                                            <td className="font-medium">{p.plan_name}</td>
                                            <td className="font-mono">{p.hashrate_ghs} GH/s</td>
                                            <td className="text-[var(--text-3)]">{new Date(p.purchased_at).toLocaleDateString()}</td>
                                            <td className="text-[var(--text-3)]">{new Date(p.expires_at).toLocaleDateString()}</td>
                                            <td className="font-mono">{fmtAone(p.total_earned, 2)}</td>
                                            <td className="font-mono text-[var(--primary)]">{fmtAone(p.pending_aone, 4)}</td>
                                            <td>
                                                <span className={`chip !text-[10.5px] !py-0.5 ${p.status === "active" ? "chip-live" : ""}`}>
                                                    {p.status}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </section>
            )}

            {/* Not signed in CTA */}
            {!user && (
                <section className="pb-16 px-5 sm:px-8">
                    <div className="max-w-[1200px] mx-auto card p-8 text-center">
                        <h3 className="text-[22px] font-bold text-[var(--text)]">Sign in to start mining</h3>
                        <p className="text-[14px] text-[var(--text-2)] mt-2">
                            Create an account, deposit USDT from any chain, and pick a plan.
                        </p>
                        <button onClick={onConnect} className="btn-primary !text-[14px] mt-6" data-testid="miner-signin-cta">
                            Get Started <ArrowRight size={13} />
                        </button>
                    </div>
                </section>
            )}
        </div>
    );
}

function NetStat({ label, value, sub, live }) {
    return (
        <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-[var(--text-3)] flex items-center gap-1.5">
                {label}
                {live && <span className="chip chip-live !py-0 !text-[9px]">LIVE</span>}
            </div>
            <div className="text-[18px] font-semibold text-[var(--text)] tabular-nums mt-1">{value}</div>
            {sub && <div className="text-[11px] font-mono text-[var(--text-3)] mt-0.5">{sub}</div>}
        </div>
    );
}

function DashCard({ label, value, icon, action, live }) {
    return (
        <div className="card p-5">
            <div className="flex items-center justify-between">
                <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-[var(--text-3)] flex items-center gap-1.5">
                    {icon}
                    {label}
                </div>
                {live && <span className="chip chip-live !py-0 !text-[9.5px]">LIVE</span>}
            </div>
            <div className="text-[22px] font-semibold text-[var(--text)] tabular-nums mt-2 font-mono">{value}</div>
            {action && <div className="mt-1">{action}</div>}
        </div>
    );
}

function PlanCard({ plan, onBuy, buying, disabled, signedIn, onSignIn }) {
    const featured = plan.id === "pro";
    return (
        <div
            data-testid={`miner-plan-${plan.id}`}
            className={`card p-7 relative ${featured ? "border-2" : ""}`}
            style={featured ? { borderColor: "var(--primary)" } : {}}
        >
            {featured && (
                <div className="absolute -top-3 left-6 chip chip-live text-[10px] !py-0.5">
                    Most Popular
                </div>
            )}
            <div className="text-[13px] font-mono uppercase tracking-[0.14em] text-[var(--text-3)]">{plan.name}</div>
            <div className="mt-3 flex items-baseline gap-1">
                <span className="text-[38px] font-bold text-[var(--text)]">${plan.price_usdt}</span>
                <span className="text-[13px] text-[var(--text-3)]">USDT</span>
            </div>
            <p className="text-[12.5px] text-[var(--text-3)] mt-1 leading-snug">{plan.tagline}</p>
            <div className="mt-5 space-y-2.5 text-[13.5px]">
                <Bullet>
                    <span className="font-mono">{plan.hashrate_ghs} GH/s</span> hashrate
                </Bullet>
                <Bullet>
                    <span className="font-mono">~{plan.daily_aone.toLocaleString()}</span> AONE/day
                </Bullet>
                <Bullet>
                    <span className="font-mono">{plan.duration_days}-day</span> mining contract
                </Bullet>
                <Bullet>Instant AONE claims · zero fees</Bullet>
            </div>
            <button
                data-testid={`miner-buy-${plan.id}`}
                onClick={signedIn ? onBuy : onSignIn}
                disabled={disabled}
                className={`w-full justify-center mt-6 !py-3 !text-[14px] ${featured ? "btn-primary" : "btn-dark"}`}
            >
                {buying ? "Activating…" : signedIn ? `Buy ${plan.name}` : "Sign in to buy"}
                <ArrowRight size={13} />
            </button>
        </div>
    );
}

function Bullet({ children }) {
    return (
        <div className="flex items-center gap-2 text-[var(--text-2)]">
            <CheckCircle size={14} weight="fill" className="text-[var(--primary)] shrink-0" />
            <span>{children}</span>
        </div>
    );
}
